import { newId, nowIso } from "@/lib/uid";
import type { AuditCategory, AuditLogEntry } from "@/types/schemas";

import { db } from "./schema";

/**
 * Append-only audit log for catalog memory. Major actions (uploads, exports,
 * bulk edits, profile/config changes, deletes/restores, compares, quality
 * actions) are recorded here so the user can see — and export — exactly what
 * happened, all locally.
 */

/** Default audit-log retention, in days (overridable per profile). */
export const DEFAULT_AUDIT_RETENTION_DAYS = 90;

/** Record an action. `id` and `timestamp` are assigned here. */
export async function logAction(
  entry: Omit<AuditLogEntry, "id" | "timestamp">,
): Promise<void> {
  const record: AuditLogEntry = { ...entry, id: newId(), timestamp: nowIso() };
  await db.auditLog.add(record);
}

/** Filters for {@link queryLog}. */
export interface AuditQuery {
  profileId?: string;
  category?: AuditCategory;
  /** Inclusive lower bound (ISO timestamp). */
  since?: string;
  /** Exclusive upper bound (ISO timestamp). */
  until?: string;
  /** Cap on the number of (newest-first) entries returned. */
  limit?: number;
}

/**
 * Query the audit log, newest first.
 *
 * Args:
 *     query: Optional profile, category, time-range, and limit filters.
 *
 * Returns:
 *     Matching entries sorted by timestamp descending.
 */
export async function queryLog(query: AuditQuery = {}): Promise<AuditLogEntry[]> {
  const { profileId, category, since, until, limit } = query;

  const collection =
    profileId !== undefined
      ? db.auditLog.where("profile_id").equals(profileId)
      : db.auditLog.toCollection();

  const entries = await collection.sortBy("timestamp");
  entries.reverse(); // newest first

  const filtered = entries.filter((e) => {
    if (category !== undefined && e.category !== category) return false;
    if (since !== undefined && e.timestamp < since) return false;
    if (until !== undefined && e.timestamp >= until) return false;
    return true;
  });

  return limit !== undefined ? filtered.slice(0, limit) : filtered;
}

const CSV_COLUMNS = ["timestamp", "category", "action", "profile_id", "details"] as const;

function escapeCsv(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function toCsv(entries: AuditLogEntry[]): string {
  const header = CSV_COLUMNS.join(",");
  const lines = entries.map((e) =>
    [
      e.timestamp,
      e.category,
      e.action,
      e.profile_id ?? "",
      JSON.stringify(e.details),
    ]
      .map((cell) => escapeCsv(String(cell)))
      .join(","),
  );
  return [header, ...lines].join("\r\n");
}

async function toPdf(entries: AuditLogEntry[]): Promise<Blob> {
  // Lazy-load jspdf so it stays out of the main bundle (only pulled for exports).
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 40;
  let y = margin;

  doc.setFontSize(14);
  doc.text("CatalogKit — Audit Log", margin, y);
  y += 20;
  doc.setFontSize(9);
  doc.text(`Exported ${nowIso()} · ${entries.length} entries`, margin, y);
  y += 20;

  const pageHeight = doc.internal.pageSize.getHeight();
  for (const e of entries) {
    const line = `${e.timestamp}  [${e.category}]  ${e.action}`;
    for (const wrapped of doc.splitTextToSize(line, 515) as string[]) {
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(wrapped, margin, y);
      y += 12;
    }
  }
  return doc.output("blob");
}

/**
 * Export the audit log as a downloadable Blob.
 *
 * Args:
 *     profileId: Restrict to one profile, or `undefined` for all entries.
 *     format: `"csv"`, `"json"`, or `"pdf"`.
 *
 * Returns:
 *     A Blob with the appropriate MIME type.
 */
export async function exportLog(
  profileId: string | undefined,
  format: "csv" | "json" | "pdf",
): Promise<Blob> {
  const entries = await queryLog(profileId !== undefined ? { profileId } : {});
  switch (format) {
    case "csv":
      return new Blob([toCsv(entries)], { type: "text/csv;charset=utf-8" });
    case "json":
      return new Blob([JSON.stringify(entries, null, 2)], {
        type: "application/json",
      });
    case "pdf":
      return toPdf(entries);
  }
}

function cutoffIso(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * Delete audit entries older than the retention window.
 *
 * Per-profile retention is respected: a profile whose `retention_days` is set
 * uses that value; everything else (and global, profile-less entries) uses
 * `retentionDays`.
 *
 * Args:
 *     retentionDays: Default retention window in days.
 *
 * Returns:
 *     The number of entries deleted.
 */
export async function pruneOldEntries(
  retentionDays: number = DEFAULT_AUDIT_RETENTION_DAYS,
): Promise<{ deleted: number }> {
  const profiles = await db.profiles.toArray();
  const perProfile = new Map<string, number>();
  for (const p of profiles) {
    if (p.retention_days != null) perProfile.set(p.id, p.retention_days);
  }

  const entries = await db.auditLog.toArray();
  const doomed = entries
    .filter((e) => {
      const days =
        e.profile_id !== undefined ? (perProfile.get(e.profile_id) ?? retentionDays) : retentionDays;
      return e.timestamp < cutoffIso(days);
    })
    .map((e) => e.id);

  await db.auditLog.bulkDelete(doomed);
  return { deleted: doomed.length };
}
